import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card, Button, Select } from '../ui';

/**
 * CustomerAppointments component displays a customer's appointment history
 * and allows creating new appointments
 */
const CustomerAppointments = ({ customerId, appointments = [], loading = false, onCreateAppointment = () => { } }) => {
  const { hasPermission } = useAuth();
  const canCreateAppointment = hasPermission('appointments', 'write');

  const [filter, setFilter] = useState('upcoming');

  // Sort and filter appointments
  const filteredAppointments = useMemo(() => {
    const now = new Date();
    let filtered = [...appointments];

    // Sort by date (newest first for past, soonest first for upcoming)
    filtered.sort((a, b) => {
      const dateA = new Date(a.appointmentTime);
      const dateB = new Date(b.appointmentTime);
      if (filter === 'upcoming') {
        return dateA - dateB; // Ascending for upcoming
      } else {
        return dateB - dateA; // Descending for past
      }
    });

    // Filter by date
    if (filter === 'upcoming') {
      return filtered.filter(app => new Date(app.appointmentTime) >= now);
    } else if (filter === 'past') {
      return filtered.filter(app => new Date(app.appointmentTime) < now);
    }
    return filtered;
  }, [appointments, filter]);

  // Format date and time for display
  const formatDateTime = (dateString) => {
    if (!dateString) return '未记录';
    const date = new Date(dateString);

    const dateFormat = new Intl.DateTimeFormat('zh-CN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'long'
    });

    const timeFormat = new Intl.DateTimeFormat('zh-CN', {
      hour: '2-digit',
      minute: '2-digit'
    });

    return `${dateFormat.format(date)} ${timeFormat.format(date)}`;
  };

  // Status badge component
  const StatusBadge = ({ status }) => {
    const statusConfig = {
      scheduled: {
        className: 'bg-blue-100 text-blue-800 border-blue-200',
        label: '已预约',
        icon: (
          <svg className="h-3 w-3 mr-1" fill="currentColor" viewBox="0 0 8 8">
            <circle cx="4" cy="4" r="3" />
          </svg>
        )
      },
      confirmed: {
        className: 'bg-emerald-100 text-emerald-800 border-emerald-200',
        label: '已签到',
        icon: (
          <svg className="h-3 w-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        )
      },
      completed: {
        className: 'bg-purple-100 text-purple-800 border-purple-200',
        label: '已完成',
        icon: (
          <svg className="h-3 w-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4" />
          </svg>
        )
      },
      cancelled: {
        className: 'bg-red-100 text-red-800 border-red-200',
        label: '已取消',
        icon: (
          <svg className="h-3 w-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        )
      },
      noshow: {
        className: 'bg-gray-100 text-gray-800 border-gray-200',
        label: '未到诊',
        icon: (
          <svg className="h-3 w-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01" />
          </svg>
        )
      }
    };

    const config = statusConfig[status] || statusConfig.scheduled;

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${config.className}`}>
        {config.icon}
        {config.label}
      </span>
    );
  };

  // Filter options
  const filterOptions = [
    { value: 'upcoming', label: '即将到来' },
    { value: 'past', label: '历史预约' },
    { value: 'all', label: '全部预约' }
  ];

  return (
    <Card variant="modern" className="border-emerald-100">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div className="flex items-center space-x-3 mb-4 sm:mb-0">
          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center shadow-lg">
            <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              预约记录
            </h3>
            <p className="text-gray-600 text-sm">
              查看预约历史和即将到来的预约
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          <div className="w-36">
            <Select
              options={filterOptions}
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              size="small"
            />
          </div>
          {canCreateAppointment && (
            <Button
              onClick={() => onCreateAppointment(customerId)}
              disabled={loading}
              loading={loading}
              size="small"
            >
              <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              新预约
            </Button>
          )}
        </div>
      </div>

      {/* Appointments List */}
      {filteredAppointments.length > 0 ? (
        <div className="space-y-3">
          {filteredAppointments.map((appointment) => (
            <Link
              key={appointment.id}
              to={`/appointments/${appointment.id}`}
              className="block group"
            >
              <div className="bg-white border border-gray-200 rounded-xl p-4 hover:border-emerald-300 hover:shadow-lg transition-all duration-200 group-hover:bg-emerald-50/30">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-lg flex items-center justify-center text-white font-medium text-sm shadow-md">
                      {appointment.treatmentName ? appointment.treatmentName.charAt(0) : 'T'}
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 group-hover:text-emerald-700 transition-colors duration-200">
                        {appointment.treatmentName || '普通调理'}
                      </h4>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {appointment.venueName || '未指定地点'}
                      </p>
                    </div>
                  </div>
                  <StatusBadge status={appointment.status} />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center text-gray-600">
                    <svg className="h-4 w-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <span>{appointment.practitionerName || '未指定调理师'}</span>
                  </div>

                  <div className="flex items-center text-gray-600">
                    <svg className="h-4 w-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>{appointment.duration ? `${appointment.duration}分钟` : '未设定时长'}</span>
                  </div>
                </div>

                <div className="mt-3 pt-3 border-t border-gray-100">
                  <div className="flex items-center text-sm text-gray-600">
                    <svg className="h-4 w-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span className="font-medium">{formatDateTime(appointment.appointmentTime)}</span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="h-8 w-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {filter === 'upcoming' ? '无即将到来的预约' : filter === 'past' ? '无历史预约记录' : '无预约记录'}
          </h3>
          <p className="text-gray-500 mb-6">
            {filter === 'upcoming'
              ? '该客户目前没有即将到来的预约'
              : filter === 'past'
                ? '该客户目前没有历史预约记录'
                : '该客户目前没有任何预约记录'
            }
          </p>
          {canCreateAppointment && (
            <Button onClick={() => onCreateAppointment(customerId)}>
              <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              创建新预约
            </Button>
          )}
        </div>
      )}
    </Card>
  );
};

CustomerAppointments.propTypes = {
  customerId: PropTypes.string.isRequired,
  appointments: PropTypes.array.isRequired,
  onCreateAppointment: PropTypes.func.isRequired,
  loading: PropTypes.bool
};

export default CustomerAppointments;